import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Alert,
  AlertTitle,
  Collapse,
  IconButton,
  Fab,
  Zoom,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Info as InfoIcon,
  Dashboard as DashboardIcon,
  Assignment as AssignmentIcon,
  Search as SearchIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import RMACheck from '../components/RMACheck';
import RMANew from '../components/RMANew';
import ServicemanRMAReview from '../components/ServicemanRMAReview';
import RMAReviewSystem from '../components/RMAReviewSystem';
import CustomerRMAReviewSystem from '../components/CustomerRMAReviewSystem';
import CustomerRMADetailsView from '../components/CustomerRMADetailsView';
import RMADetailsView from '../components/RMADetailsView';

// Custom theme (unchanged)
const theme = createTheme({
    palette: {
    primary: { main: '#126F42', light: '#6F886A', dark: '#0d5232' },
    secondary: { main: '#373D3E', light: '#5a6263', dark: '#232829' },
    background: { default: '#FFFFFF', paper: '#FFFFFF' },
    success: { main: '#126F42' },
    warning: { main: '#f59e0b' },
    error: { main: '#ef4444' },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 700 },
    h2: { fontWeight: 600 },
    h3: { fontWeight: 600 },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: { root: { borderRadius: 12 } },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 8, textTransform: 'none', fontWeight: 600 },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: { '& .MuiOutlinedInput-root': { borderRadius: 8 } },
      },
    },
  },
});

const RMALandingPage = ({ user }) => {
  const { rmaId, action } = useParams();
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [messageOpen, setMessageOpen] = useState(false);
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const handleMessage = (text, type = 'info') => {
    setMessage(text);
    setMessageType(type);
    setMessageOpen(true);
    setTimeout(() => setMessageOpen(false), 5000);
  };

  const clearMessage = () => {
    setMessageOpen(false);
    setTimeout(() => setMessage(''), 300);
  };

  const getMessageSeverity = (type) => {
    switch (type) {
      case 'success': return 'success';
      case 'error': return 'error';
      case 'warning': return 'warning';
      default: return 'info';
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const handleEdit = (rmaId, mode = 'edit') => {
    navigate(`/rma/${rmaId}/${mode}`);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          background: '#FFFFFF',
          position: 'relative',
          pb: 2,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.03,
            backgroundImage: '#FFFFFF',
            backgroundSize: '50px 50px',
            zIndex: 0,
          }}
        />
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Header user={user} />
          <Container maxWidth="xl" sx={{ pt: 2, px: { xs: 2, sm: 3 } }}>
            <Box sx={{ mb: 2, textAlign: 'center' }}>
              <Typography
                variant="h5"
                component="h1"
                gutterBottom
                sx={{
                  color: '#126F42',
                  fontWeight: 'bold',
                  textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  mb: 1,
                }}
              >
                {user.user_type === 'serviceman' ? (
                  <>
                    <DashboardIcon sx={{ mr: 1, fontSize: 'inherit' }} />
                    Serviceman Dashboard
                  </>
                ) : rmaId && action === 'edit' ? (
                  <>
                    <AssignmentIcon sx={{ mr: 1, fontSize: 'inherit' }} />
                    Edit RMA
                  </>
                ) : rmaId ? (
                  <>
                    <AssignmentIcon sx={{ mr: 1, fontSize: 'inherit' }} />
                    RMA Details
                  </>
                ) : (
                  <>
                    <AssignmentIcon sx={{ mr: 1, fontSize: 'inherit' }} />
                    RMA Management
                  </>
                )}
              </Typography>
            </Box>

            {user.user_type === 'serviceman' ? (
              <Box sx={{ width: '100%' }}>
                <Card
                  elevation={3}
                  sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    width: '100%',
                  }}
                >
                  <CardContent sx={{ p: 0 }}>

  <Box sx={{ bgcolor: '#126F42', px: 2, py: 1.5 }}>
    <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
      RMA Review Queue
    </Typography>
  </Box>
  <Box sx={{ p: 2 }}>
    <ServicemanRMAReview />
  </Box>
</CardContent>
                </Card>
              </Box>
            ) : rmaId && action === 'edit' ? (
              <Box>
                {user.user_type === 'customer' ? (
                  <CustomerRMAReviewSystem
                    rmaId={rmaId}
                    onBack={handleBack}
                  />
                ) : (
                  <RMAReviewSystem
                    rmaId={rmaId}
                    onBack={handleBack}
                    userType={user.user_type}
                  />
                )}
              </Box>
            ) : rmaId ? (
              <Box>
                {user.user_type === 'customer' ? (
                  <CustomerRMADetailsView
                    rmaId={rmaId}
                    onBack={handleBack}
                  />
                ) : (
                  <RMADetailsView
                    rmaId={rmaId}
                    onBack={handleBack}
                    onEdit={handleEdit}
                    onDownload={() => console.log('Downloading RMA:', rmaId)}
                    refreshTrigger={0}
                    userType={user.user_type}
                  />
                )}
              </Box>
            ) : (
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Zoom in timeout={500}>
                      <Card
                        elevation={3}
                        sx={{
                          borderRadius: 2,
                          height: '100%',
                          transition: 'transform 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
                          },
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>

                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#475569' }}>
                              Check RMA Status
                            </Typography>
                          </Box>
                          <Divider sx={{ mb: 2 }} />
                          <RMACheck onMessage={handleMessage} navigate={navigate} />
                        </CardContent>
                      </Card>
                    </Zoom>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Zoom in timeout={700}>
                      <Card
                        elevation={3}
                        sx={{
                          borderRadius: 2,
                          height: '100%',
                          transition: 'transform 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
                          },
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>

                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#475569' }}>
                              Create New RMA
                            </Typography>
                          </Box>
                          <Divider sx={{ mb: 2 }} />
                          <RMANew onMessage={handleMessage} navigate={navigate} userType={user.user_type} />
                        </CardContent>
                      </Card>
                    </Zoom>
                  </Grid>
                </Grid>
              </Box>
            )}

            {message && (
              <Box
                sx={{
                  position: 'fixed',
                  top: 20,
                  right: 20,
                  zIndex: 1300,
                  minWidth: 300,
                  maxWidth: 500,
                }}
              >
                <Collapse in={messageOpen}>
                  <Alert
                    severity={getMessageSeverity(messageType)}
                    action={
                      <IconButton
                        aria-label="close"
                        color="inherit"
                        size="small"
                        onClick={clearMessage}
                      >
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    }
                    sx={{
                      boxShadow: 4,
                      borderRadius: 2,
                    }}
                  >
                    <AlertTitle>
                      {messageType === 'success'
                        ? 'Success'
                        : messageType === 'error'
                        ? 'Error'
                        : messageType === 'warning'
                        ? 'Warning'
                        : 'Information'}
                    </AlertTitle>
                    {message}
                  </Alert>
                </Collapse>
              </Box>
            )}
          </Container>
        </Box>

        {isMobile && user.user_type !== 'serviceman' && (
          <Fab
            color="primary"
            aria-label="View additional information"
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              zIndex: 1200,
            }}
          >
            <InfoIcon />
          </Fab>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default RMALandingPage;